import { useMemo, useState, useEffect, useCallback } from 'react';
import { useClientAccess, ClientType } from './useClientAccess';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// Free content: Module 1, Class 1 from the first academy
const FREE_ACADEMY_ID = 'faceless-content';
const FREE_MODULE_NUMBER = 1;
const FREE_CLASS_NUMBER = 1;

interface CompletedModule {
  academyId: string;
  moduleNumber: number;
}

export interface FreemiumAccessResult {
  hasFullAccess: boolean;
  isContentFree: (academyId: string, moduleNumber: number, classNumber?: number) => boolean;
  subscriptionStatus: string;
  loading: boolean;
  clientType: ClientType;
  canAccessModule: (academyId: string, moduleNumber: number) => boolean;
  canAccessClass: (academyId: string, moduleNumber: number, classNumber: number) => boolean;
  completedModules: CompletedModule[];
  isMonthlyPlan: boolean;
  isLifetimePlan: boolean;
  getNextRequiredModule: (academyId: string, moduleNumber: number) => number | null;
}

export const useFreemiumAccess = (): FreemiumAccessResult => {
  const { user } = useAuth();
  const { 
    loading, 
    clientInfo, 
    canAccessModule: baseCanAccessModule, 
    isContentFree: checkContentFree 
  } = useClientAccess();

  const [completedModules, setCompletedModules] = useState<CompletedModule[]>([]);
  const [loadingCompletions, setLoadingCompletions] = useState(true);

  // Fetch completed modules for the current user (with academy info)
  useEffect(() => {
    const fetchCompletedModules = async () => {
      if (!user) {
        setLoadingCompletions(false);
        return;
      }

      try {
        const { data: clientData } = await supabase
          .from('clients')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!clientData) {
          setLoadingCompletions(false);
          return;
        }

        const { data: completions } = await supabase
          .from('client_module_completions')
          .select('module_id, academy_id')
          .eq('client_id', clientData.id);

        if (completions) {
          setCompletedModules(completions.map(c => ({
            academyId: c.academy_id,
            moduleNumber: c.module_id
          })));
        }
      } catch (error) {
        console.error('Error fetching completions:', error);
      } finally {
        setLoadingCompletions(false);
      }
    };

    fetchCompletedModules();
  }, [user]);

  const isContentFree = useMemo(() => {
    return (academyId: string, moduleNumber: number, classNumber?: number): boolean => {
      // Module 1, Class 1 of the first academy is always free
      if (academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER) {
        // If checking a specific class, only class 1 is free
        if (classNumber !== undefined) {
          return classNumber === FREE_CLASS_NUMBER;
        }
        // If just checking module access for viewing the module page, allow it
        return true;
      }
      return false;
    };
  }, []);

  // Check if user is on a monthly-type plan (including test_monthly which mirrors monthly exactly)
  const isMonthlyPlan = useMemo(() => {
    return clientInfo.clientType === 'monthly' || clientInfo.clientType === 'test_monthly';
  }, [clientInfo.clientType]);

  // Check if user is on a lifetime-type plan (including test_lifetime which mirrors lifetime exactly)
  const isLifetimePlan = useMemo(() => {
    return clientInfo.clientType === 'lifetime' || clientInfo.clientType === 'test_lifetime';
  }, [clientInfo.clientType]);

  // Full access means paid user (lifetime or monthly - NOT freemium)
  // Both monthly and test_monthly have "full access" but with completion-based unlock
  const hasFullAccess = useMemo(() => {
    const fullAccessTypes: ClientType[] = ['admin', 'lifetime', 'test_lifetime', 'monthly', 'test_monthly'];
    return fullAccessTypes.includes(clientInfo.clientType);
  }, [clientInfo.clientType]);

  // Wrapper for canAccessModule that passes completed modules for monthly users
  const canAccessModule = useCallback((academyId: string, moduleNumber: number): boolean => {
    return baseCanAccessModule(academyId, moduleNumber, completedModules);
  }, [baseCanAccessModule, completedModules]);

  // Wrapper for canAccessClass that mirrors monthly behavior exactly (completion-based module unlock)
  const canAccessClass = useCallback((academyId: string, moduleNumber: number, classNumber: number): boolean => {
    // Admin/Lifetime/Test_Lifetime have full access
    if (clientInfo.clientType === 'admin' || clientInfo.clientType === 'lifetime' || clientInfo.clientType === 'test_lifetime') {
      return true;
    }

    // Monthly/Test_Monthly: if module is accessible, all classes in it are accessible
    if (clientInfo.clientType === 'monthly' || clientInfo.clientType === 'test_monthly') {
      return baseCanAccessModule(academyId, moduleNumber, completedModules);
    }

    // Freemium: ONLY Class 1 of Module 1 of Academy 1
    if (clientInfo.clientType === 'freemium') {
      return academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER && classNumber === FREE_CLASS_NUMBER;
    }

    return false;
  }, [clientInfo.clientType, baseCanAccessModule, completedModules]);

  // Get the module number that needs to be completed to unlock a target module
  const getNextRequiredModule = useCallback((academyId: string, targetModule: number): number | null => {
    if (targetModule <= 1) return null;
    
    // Find the highest completed module in this academy that's less than target
    const relevantCompleted = completedModules
      .filter(m => m.academyId === academyId && m.moduleNumber < targetModule)
      .map(m => m.moduleNumber);
    const highestCompleted = relevantCompleted.length > 0 ? Math.max(...relevantCompleted) : 0;
    
    // The next required module is the one after the highest completed
    const nextRequired = highestCompleted + 1;
    
    // If next required is less than target, return it
    return nextRequired < targetModule ? nextRequired : targetModule - 1;
  }, [completedModules]);

  return {
    hasFullAccess,
    isContentFree,
    subscriptionStatus: clientInfo.plan || 'inactive',
    loading: loading || loadingCompletions,
    clientType: clientInfo.clientType,
    canAccessModule,
    canAccessClass,
    completedModules,
    isMonthlyPlan,
    isLifetimePlan,
    getNextRequiredModule,
  };
};
